import React, { useEffect, useState } from "react";
import Loader from "./components/Loader";
import {
  Navigate,
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ToastContainer } from "./toast";

import studentRoutes from "./Routes/StudentRoutes";
import adminRoutes from "./Routes/StaffsRoutes";
import publicRoutes from "./Routes/PublicRoutes";
import authRoutes from "./Routes/AuthRoutes";

import { auth } from "./utils/firebase";
import SuperAdminRoutes from "./Routes/Admin/SuperAdminRoutes.jsx";
import StudentOnboarding from "./Modules/Student/Screens/StudentOnboarding";
import Admin from "./Modules/Admin/Screens/Dashboards/SuperAdminDashboard";
import StaffOnboarding from "./Modules/Admin/Screens/Staffs/StaffOnboarding";
 import BatchAnalysis from "./Modules/Admin/Screens/Reports/BatchAnalysis";
import Assignments from "./Modules/Assignments/Screens/Assignments";

import {
  useGetAdminUsingEmailQuery,
  useGetStudentByEmailIdQuery,
} from "./graphql/generated.tsx";

import AssignmentRoutes from "./Routes/Admin/AssignmentRoutes";
import CourseRoutes from "./Routes/Admin/CourseRoutes";
import ReportRoutes from "./Routes/Admin/ReportRoutes";
import StudentCreationRoutes from "./Routes/Admin/StudentCreationRoutes";
import ManageCampus from "./Routes/Admin/ManageCampus.jsx";
import ManageClusterRoutes from "./Routes/Admin/ManageClusterRoutes.jsx";
import ModuleRoutes from "./Routes/Admin/ModuleRoutes";
import WorkInstructionSlide from "./Modules/Admin/Screens/Student_Creation/WorkInstructionSlide.jsx";

const App = () => {
  return (
    <div className="w-full App">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
        style={{ zIndex: 10001 }}
      />
      <Router>
        <Routes>
          {/* ✅ Public routes */}
          {publicRoutes}
          {authRoutes}
        </Routes>

        {/* ✅ Protected routes */}
        <ProtectedRoutes />
      </Router>
    </div>
  );
};

const ProtectedRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentPage = location.pathname;

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUser(user);
        user.getIdToken().then((token) => {
          localStorage.setItem("idtoken", token);
        });
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  let userRole = null;
  let flogin = null;
  try {
    userRole = user?.reloadUserInfo?.customAttributes
      ? JSON.parse(user?.reloadUserInfo?.customAttributes)?.role
      : null;
    flogin = user?.reloadUserInfo?.customAttributes
      ? JSON.parse(user?.reloadUserInfo?.customAttributes)?.flogin
      : null;
  } catch (error) {
    console.error("Failed to parse user role:", error);
  }

  const email = JSON.parse(localStorage.getItem("user"))?.email;

  const { data: admin } = useGetAdminUsingEmailQuery({
    variables: { email },
    skip: userRole !== "bt-admin",
  });

  const { data: student } = useGetStudentByEmailIdQuery({
    variables: { email },
    skip: userRole !== "student",
  });

  const studentDetails = student?.getStudentByEmailId;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        
      </div>
    );
  }

  // ✅ allowlist for unauthenticated routes
  const publicAllowList = [
    "/login",
    "/forgetPassword",
    "/terms",
    "/contactus",
    "/privacy",
    "/password-reset",
    "/auth/action",
  ];

  if (!user && !publicAllowList.includes(currentPage)) {
    return <Navigate to="/login" replace />;
  }

  // ✅ Check if user needs onboarding and redirect if accessing non-onboarding routes (skip for public routes)
  if (flogin && userRole === "student" && studentDetails?.id && !currentPage.includes('/student/student-onboarding') && !publicAllowList.includes(currentPage)) {
    return <Navigate to={`/student/student-onboarding/${studentDetails.id}`} replace />;
  }

  // ✅ Check if student profile is complete (only if NOT in onboarding or public routes)
  // Redirect to profile page with completion required flag instead of showing modal
  if (!flogin && userRole === "student" && studentDetails && currentPage !== '/student/my-profile' && !publicAllowList.includes(currentPage)) {
    const hasName = !!studentDetails.name;
    const hasRollNo = !!studentDetails.rollNo;
    const hasLeetcodeUsername = !!studentDetails.leetCodeUname;
    const leetcodename = studentDetails.leetCodeUname || "";
    const isLeetcodeValid = leetcodename.length >= 3 && leetcodename.length <= 40;
    const hasDepartment = !!studentDetails.department;
    
    const isProfileIncomplete = !hasName || !hasRollNo || !hasLeetcodeUsername || !isLeetcodeValid || !hasDepartment;
    
    if (isProfileIncomplete) {
      return <Navigate to="/student/my-profile?complete=required" replace />;
    }
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          userRole === "student"
            ? user && (
                <Navigate
                  to={
                    studentDetails?.clusterIds?.length === 1
                      ? `/student/courses/${studentDetails?.clusterIds[0]}`
                      : "/student/courses"
                  }
                />
              )
            : userRole === "staff"
            ? user && <Navigate to="/staff-data-instruction" />
            : user && <Navigate to="/admin/courses" />
        }
      />

      <Route
        path="/assignments"
        element={user ? <Assignments /> : <Navigate to="/login" />}
      />


      {(userRole === "bt-admin" || userRole === "student") && [...studentRoutes]}
      {(userRole === "bt-admin" || userRole === "staff") && [...adminRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...CourseRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...AssignmentRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...ReportRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...StudentCreationRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...ManageCampus]}
      {userRole === "bt-admin" && [...ManageClusterRoutes]}

      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...SuperAdminRoutes]}

      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.isSuperAdmin &&
        [...ModuleRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.permissions?.assignments?.length > 0 &&
        [...ModuleRoutes]}

      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.permissions.courses?.length > 0 &&
        [...CourseRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.permissions.assignments?.length > 0 &&
        [...AssignmentRoutes]}
      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.permissions.reports &&
        [...ReportRoutes]}

      {userRole === "bt-admin" &&
        admin?.getAdminUsingEmail?.permissions.student_creation &&
        [...StudentCreationRoutes]}

      {/* Hidden BatchAnalysis route - uncomment to restore */}
      {/* {userRole === "staff" && (
        <Route key="admin" path="/admin" element={<BatchAnalysis />} />
      )} */}
      {userRole === "bt-admin" && (
        <Route key="admin" path="/admin" element={<Admin />} />
      )}
      {userRole === "student" && flogin && (
        <Route
          key="student-onboarding"
          path="/student/student-onboarding/:id"
          element={<StudentOnboarding />}
        />
      )}
      {userRole === "staff" && flogin && (
        <Route
          key="update-details"
          path="/admin/staff-onboarding/:id"
          element={<StaffOnboarding />}
        />
      )}
    </Routes>
  );
};

export default App;
